import React from 'react';
import { Button } from '@fiverr-private/button';
import { Center, Stack } from '@fiverr-private/layout_components';
import { List, ListItem, ListItemIcon } from '@fiverr-private/list';
import { Image } from '@fiverr-private/media';
import { CheckIcon, ExternalLinkIcon } from '@fiverr-private/visuals';
import { getContext } from '@fiverr-private/fiverr_context';
import { ElementType } from '@fiverr-private/bianka_js';
import { useImpressionContainer, useImpressionItem } from '@fiverr-private/impressionable';
import { stepMetaMap, onImpression } from '@fiverr-private/store_builder_bi_events';
import { AppI18n } from '../../i18n';
import { useWizardNavigation } from '../../hooks/use-wizard-navigation/useWizardNavigation';
import { reportClickEvent } from '../../utils/report-bi-event/reportClickEvent';
import { StepTitle } from '../../components/step-title/StepTitle';

const IMAGE_URL =
    'https://fiverr-res.cloudinary.com/image/upload/f_auto,q_auto/v1/attachments/generic_asset/asset/717fdd582fbabdc9c30be1eaf5d4b7c8-1750611787844/connect_autods_store.png';
const AUTODS_URL = 'https://platform.autods.com/choose-plan?ref=MzMzMjY4MA==';
const LIST_ITEMS = [
    {
        id: 1,
        text: <AppI18n k="steps.subscribe_autods.list.item_1" />,
    },
    {
        id: 2,
        text: <AppI18n k="steps.subscribe_autods.list.item_2" />,
    },
    {
        id: 3,
        text: <AppI18n k="steps.subscribe_autods.list.item_3" />,
    },
    {
        id: 4,
        text: <AppI18n k="steps.subscribe_autods.list.item_4" />,
    },
    {
        id: 5,
        text: <AppI18n k="steps.subscribe_autods.list.item_5" />,
    },
];

export const SubscribeAutods: React.FC = () => {
    const { pageCtxId, userId } = getContext();
    const { currentPath } = useWizardNavigation();
    const [pageImpressionRef, pageImpressionId] = useImpressionContainer();

    useImpressionItem({
        selfRef: pageImpressionRef,
        selfId: pageImpressionId,
        sendImpressionToBQ: false,
        enrichment: {
            eventType: stepMetaMap[currentPath].impressionName,
            componentName: stepMetaMap[currentPath].name,
        },
        intersectionThreshold: 0,
        onImpression,
    });

    const handleSubscribeAutodsClick = () => {
        reportClickEvent({
            biEventData: {
                userId,
                ctxId: pageCtxId,
            },
            eventType: 'clicked_on_autods_sign_up',
            elementType: ElementType.LINK,
            currentPath,
        });
        window.open(AUTODS_URL, '_blank');
    };

    return (
        <Center>
            <Stack direction={{ default: 'column', md: 'row' }} gap="16" ref={pageImpressionRef}>
                <Stack direction="column" gap="8" color="grey_1200">
                    <StepTitle
                        title={<AppI18n k="steps.subscribe_autods.title" />}
                        description={<AppI18n k="steps.subscribe_autods.description" />}
                    />
                    <List rowGap="4" paddingLeft="0">
                        {LIST_ITEMS.map(({ id, text }) => (
                            <ListItem key={id} marginBottom="2">
                                <Stack gap="3">
                                    <ListItemIcon marginTop="px">
                                        <Stack
                                            backgroundColor="grey_300"
                                            borderRadius="circle"
                                            width={24}
                                            height={24}
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <CheckIcon size="sm" color="grey_1200" />
                                        </Stack>
                                    </ListItemIcon>
                                    {text}
                                </Stack>
                            </ListItem>
                        ))}
                    </List>
                    <Button onClick={handleSubscribeAutodsClick} alignSelf="flexStart" variant="outline">
                        <AppI18n k="steps.subscribe_autods.cta" />
                        <ExternalLinkIcon />
                    </Button>
                </Stack>
                <Stack direction="column" gap="2" color="grey_1200">
                    <Image src={IMAGE_URL} width={{ default: 310, sm: 488 }} borderRadius="lg" alt="AutoDS logo" />
                </Stack>
            </Stack>
        </Center>
    );
};

SubscribeAutods.displayName = 'SubscribeAutods';
